import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box, Button, TableCell, TableRow, Chip, Autocomplete, TextField,
    Paper, IconButton, Checkbox, Snackbar, Alert,
    Typography, FormControlLabel
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import PaymentIcon from '@mui/icons-material/Payment';
import DataTable from '../DataTable';
import BillDetailDialog from '../BillDetailDialog';
import * as API from '../../api';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatDateDisplay } from '../../utils/dateUtils';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import CustomDatePicker from '../CustomDatePicker';
import BulkPaymentDialog from '../BulkPaymentDialog';
import PayableInfoDialog from './PayableInfoDialog';
import ConfirmDialog from '../ConfirmDialog';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ReportExportDialog from './ReportExportDialog'; // <--- THÊM DÒNG NÀY

const CustomLabel = ({ title }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title}
    </Typography>
);

const PAYABLE_TYPE_LABELS = {
    'TOUR': 'Tour',
    'TRANSPORT': 'Xe',
    'ROOM': 'Phòng',
    'EXTRA': 'DV khác',
    'COLLECTION': 'Thu hộ'
};

const PAYABLE_TYPE_NAME = { 
    'TOUR': 'Công nợ Tour',
    'TRANSPORT_TO': 'Công nợ Xe - Chiều đi',
    'TRANSPORT_FROM': 'Công nợ Xe - Chiều về',
    'TRANSPORT_ROUND': 'Công nợ Xe - Hai chiều',
    'ROOM': 'Công nợ Phòng',
    'EXTRA': 'Công nợ Dịch vụ khác',
    'COLLECTION': 'Thu hộ',
    'OTHER': 'Khác'
};

const PAYMENT_STATUS_LABELS = { 'UNPAID': 'Chưa TT', 'APPROVED': 'Đã duyệt', 'PAID': 'Đã TT' };
const DEFAULTS = { 
    type: ['TOUR', 'TRANSPORT', 'ROOM', 'EXTRA', 'COLLECTION'], 
    paymentStatus: ['UNPAID', 'APPROVED'],
    isAllTime: 'false' 
};

const PayableListTab = forwardRef((props, ref) => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const getListParam = (key, defaultVal) => {
        const val = searchParams.get(key);
        return val ? val.split(',') : defaultVal;
    };

    const [reportOpen, setReportOpen] = useState(false);
    const [payables, setPayables] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);

    const [uiStartDate, setUiStartDate] = useState(searchParams.get('start') || format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [uiEndDate, setUiEndDate] = useState(searchParams.get('end') || format(endOfMonth(new Date()), 'yyyy-MM-dd'));
    const [uiIsAllTime, setUiIsAllTime] = useState(searchParams.get('all_time') === 'true');
    const [uiFilterType, setUiFilterType] = useState(getListParam('type', DEFAULTS.type)); 
    const [uiFilterSupplier, setUiFilterSupplier] = useState(null);
    const [uiFilterPaymentStatus, setUiFilterPaymentStatus] = useState(getListParam('status', DEFAULTS.paymentStatus));

    const [bulkPayOpen, setBulkPayOpen] = useState(false);
    const [payableInfoOpen, setPayableInfoOpen] = useState(false);
    const [billModal, setBillModal] = useState({ open: false, data: null, bookingData: null });

    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const showToast = (message, severity = 'success') => {
        setToast({ open: true, message, severity });
    };
    const handleCloseToast = () => setToast({ ...toast, open: false });

    const [confirmDialog, setConfirmDialog] = useState({ 
        open: false, 
        title: '', 
        content: '', 
        onConfirm: null 
    });

    // --- SỬA: Khởi tạo ActiveFilters từ URL ---
    const [activeFilters, setActiveFilters] = useState({
        startDate: searchParams.get('start') || format(startOfMonth(new Date()), 'yyyy-MM-dd'),
        endDate: searchParams.get('end') || format(endOfMonth(new Date()), 'yyyy-MM-dd'),
        isAllTime: searchParams.get('all_time') === 'true',
        type: getListParam('type', DEFAULTS.type),
        supplier: null,
        paymentStatus: getListParam('status', DEFAULTS.paymentStatus)
    });

    useImperativeHandle(ref, () => ({
        resetFilters: () => {
            const defaultStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
            const defaultEnd = format(endOfMonth(new Date()), 'yyyy-MM-dd');

            setUiStartDate(defaultStart);
            setUiEndDate(defaultEnd);
            setUiIsAllTime(false);
            setUiFilterType(DEFAULTS.type);
            setUiFilterSupplier(null);
            setUiFilterPaymentStatus(DEFAULTS.paymentStatus);

            setSearchParams({ tab: 'PAYABLE' });

            const resetState = {
                startDate: defaultStart,
                endDate: defaultEnd,
                isAllTime: false,
                type: DEFAULTS.type,
                supplier: null,
                paymentStatus: DEFAULTS.paymentStatus
            };

            setActiveFilters(resetState);
            setSelectedIds([]);
            loadData(resetState);
        },
        openExportDialog: () => setReportOpen(true)
    }));

    const syncToUrl = () => {
        const params = { tab: 'PAYABLE' };
        if (uiIsAllTime) params.all_time = 'true';
        else { params.start = uiStartDate; params.end = uiEndDate; }
        if (uiFilterType.length !== DEFAULTS.type.length) params.type = uiFilterType.join(',');
        if (uiFilterPaymentStatus.length !== DEFAULTS.paymentStatus.length) params.status = uiFilterPaymentStatus.join(',');
        if (uiFilterSupplier) params.supplier_id = uiFilterSupplier.id;
        setSearchParams(params);
    };

    useEffect(() => { loadData(); }, []);

    const loadData = async (filters = null) => {
        try {
            const currentFilters = filters || {
                startDate: uiStartDate,
                endDate: uiEndDate,
                isAllTime: uiIsAllTime,
                supplier: uiFilterSupplier
            };

            const params = {};
            
            if (!currentFilters.isAllTime) {
                params.start_date = currentFilters.startDate;
                params.end_date = currentFilters.endDate;
            }
            
            if (currentFilters.supplier) {
                params.supplier = currentFilters.supplier.id;
            }

            const [payRes, supRes] = await Promise.all([
                API.getAllPayables(params), 
                API.fetchAllServiceProviders()
            ]);
            
            setPayables(payRes.data);
            setSuppliers(supRes.data);

            const urlSupplierId = searchParams.get('supplier_id');
            let foundSup = null;
            if (urlSupplierId && supRes.data.length > 0) {
                foundSup = supRes.data.find(s => s.id.toString() === urlSupplierId.toString());
                if (foundSup && !filters) setUiFilterSupplier(foundSup);
            }

        } catch (error) { console.error("Lỗi dữ liệu:", error); }
    };

    const handleToggleType = (key) => setUiFilterType(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    const handleToggleStatus = (key) => setUiFilterPaymentStatus(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    const handleToggleAllTypes = () => setUiFilterType(prev => prev.length === Object.keys(PAYABLE_TYPE_LABELS).length ? [] : Object.keys(PAYABLE_TYPE_LABELS));
    const handleToggleAllStatus = () => setUiFilterPaymentStatus(prev => prev.length === Object.keys(PAYMENT_STATUS_LABELS).length ? [] : Object.keys(PAYMENT_STATUS_LABELS));

    const selectedPayables = useMemo(() => {
        return payables.filter(p => selectedIds.includes(p.id));
    }, [payables, selectedIds]);

    const bulkActionStats = useMemo(() => {
        let totalDebt = 0;
        let totalCollection = 0;
        
        let hasDifferentSuppliers = false; 
        let hasPaidItems = false;
        let firstSupplierId = null;

        if (selectedPayables.length > 0) {
            firstSupplierId = selectedPayables[0].supplier;

            selectedPayables.forEach(p => {
                const val = parseFloat(p.amount || 0);
                if (p.payable_type === 'COLLECTION') {
                    totalCollection += Math.abs(val);
                } else {
                    totalDebt += val;
                }

                if (p.status === 'PAID') hasPaidItems = true;

                if (p.supplier !== firstSupplierId) hasDifferentSuppliers = true;
            });
        }

        const netAmount = totalDebt - totalCollection;

        return {
            count: selectedPayables.length,
            totalDebt: totalDebt,
            totalCollection: totalCollection,
            netAmount: netAmount,
            hasDifferentSuppliers,
            hasPaidItems
        };
    }, [selectedPayables]);

    const handleBulkPayClick = () => {
        if (selectedIds.length === 0) return;

        const getSupplierId = (item) => {
            if (item.supplier && typeof item.supplier === 'object') return item.supplier.id;
            return item.supplier;
        };

        const firstItem = selectedPayables[0];
        const firstSupplierId = getSupplierId(firstItem);

        const isSameSupplier = selectedPayables.every(item => getSupplierId(item) == firstSupplierId);
        if (!isSameSupplier) {
            showToast("Vui lòng chỉ chọn các khoản của CÙNG 1 Nhà Cung Cấp để thực hiện thanh toán gộp!", "error");
            return;
        }

        setBulkPayOpen(true);
    };

    const executeApprove = async (items) => {
        try {
            const payload = { 
                items: items.map(p => ({ 
                    id: p.id, 
                    payable_type: p.payable_type 
                })) 
            };
            
            await API.client.post('/management/bulk_approve_payables/', payload);
            
            showToast(`Đã duyệt thành công ${items.length} khoản!`, "success");
            setSelectedIds([]);
            loadData();
        } catch (error) {
            console.error(error);
            showToast("Lỗi khi duyệt công nợ", "error");
        } finally {
            setConfirmDialog({ ...confirmDialog, open: false });
        }
    };

    const handleBulkApprove = () => {
        if (selectedIds.length === 0) return;
        
        const validItems = selectedPayables.filter(p => 
            p.status === 'UNPAID' || 
            p.status === 'PENDING' || 
            (p.payable_type === 'COLLECTION' && p.status !== 'APPROVED' && p.status !== 'PAID' && p.status !== 'SETTLED')
        );

        if (validItems.length === 0) {
            showToast("Vui lòng chọn các khoản 'Chưa thanh toán' hoặc 'Thu hộ chưa duyệt' để thao tác.", "warning");
            return;
        }

        setConfirmDialog({
            open: true,
            title: 'Xác nhận duyệt công nợ',
            content: `Bạn có chắc chắn muốn DUYỆT ${validItems.length} khoản đã chọn? Trạng thái sẽ chuyển sang "Đã duyệt".`,
            onConfirm: () => executeApprove(validItems)
        });
    };

    const handleApplyFilter = () => {
        syncToUrl();
        
        const newFilters = {
            startDate: uiStartDate, 
            endDate: uiEndDate, 
            isAllTime: uiIsAllTime,
            type: uiFilterType, 
            supplier: uiFilterSupplier, 
            paymentStatus: uiFilterPaymentStatus
        };

        setActiveFilters(newFilters);
        setSelectedIds([]); 
        
        loadData(newFilters);
    };

    const filteredData = useMemo(() => {
        return payables.filter(p => {
            if (!activeFilters.isAllTime) {
                if (!p.start_date) return false;
                const pDate = p.start_date;
                if (pDate < activeFilters.startDate || pDate > activeFilters.endDate) return false;
            }
            const isTransportMatch = activeFilters.type.includes('TRANSPORT') && p.payable_type.includes('TRANSPORT');
            const isOtherMatch = activeFilters.type.includes(p.payable_type);
            if (!isTransportMatch && !isOtherMatch) return false;
            if (activeFilters.supplier && p.supplier !== activeFilters.supplier.id) return false;
            if (!activeFilters.paymentStatus.includes(p.status)) return false;
            return true;
        }).sort((a, b) => {
            const timeA = a.start_date ? new Date(a.start_date).getTime() : 0;
            const timeB = b.start_date ? new Date(b.start_date).getTime() : 0;
            return timeA - timeB;
        });
    }, [payables, activeFilters]);

    const summaryData = useMemo(() => {
        const total = filteredData.reduce((acc, p) => {
            const val = parseFloat(p.amount || 0);
            return acc + val;
        }, 0);
        return { supplier_name: `SL: ${filteredData.length}`, amount: formatCurrency(total) };
    }, [filteredData]);

    const handleViewBill = (payable) => {
        let finalImgUrl = payable.bill_image;
        
        if (finalImgUrl && typeof finalImgUrl === 'string' && !finalImgUrl.startsWith('http')) {
            const apiBase = API.client?.defaults?.baseURL?.replace(/\/api\/?$/, '') || 'http://127.0.0.1:8000';
            
            const path = finalImgUrl.startsWith('/') ? finalImgUrl : `/${finalImgUrl}`;
            finalImgUrl = `${apiBase}${path}`;
        }

        setBillModal({
            open: true,
            data: { 
                type: PAYABLE_TYPE_NAME[payable.payable_type] || payable.payable_type || '---',
                amount: payable.amount, 
                note: payable.note, 
                imageUrl: finalImgUrl, 
                supplierName: payable.supplier_name, 
                status: payable.status,
                payment_account_name: payable.payment_account_name,
                receiver_info: payable.receiver_info,      
                receive_account_name: payable.receive_account_name,
                sender_info: payable.sender_info,
                transaction_date: payable.paid_at || payable.created_at || null
            },
            bookingData: { 
                booking_code: payable.booking_code, 
                customer: { name: payable.customer_name }, 
                start_date: payable.start_date,
                end_date: payable.end_date, 
                sale_owner_name: payable.sales_owner 
            }
        });
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) setSelectedIds(filteredData.map((n) => n.id));
        else setSelectedIds([]);
    };

    const columns = [
        { label: (<Checkbox color="primary" size="small" sx={{ p: 0 }} indeterminate={selectedIds.length > 0 && selectedIds.length < filteredData.length} checked={filteredData.length > 0 && selectedIds.length === filteredData.length} onChange={handleSelectAll} />), minWidth: 40 },
        { 
            label: 'Booking',
            field: 'booking_code', 
            minWidth: 150,
            format: (value, row) => (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#007373' }}>
                        {value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#555', display: 'block', lineHeight: 1 }}>
                        {row.customer_name}
                    </Typography>
                </Box>
            )
        },
        { label: 'Ngày đi', field: 'start_date', minWidth: 100 },
        { 
            field: 'payable_type', 
            label: 'Loại', 
            minWidth: 70,
            format: (value) => (
                <Chip 
                    label={PAYABLE_TYPE_LABELS[value] || value}
                    color={value === 'COLLECTION' ? 'secondary' : 'default'} 
                    size="small" 
                    variant="outlined" 
                    sx={value === 'COLLECTION' ? { borderColor: '#9c27b0', color: '#9c27b0', fontWeight: 'bold' } : {}}
                />
            )
        },
        { 
            label: 'Ghi chú', 
            field: 'note', 
            minWidth: 180,
            format: (value, row) => {
                let displayText = value || '';
                if (row.payable_type === 'EXTRA' && row.service_name) {
                    displayText = `${row.service_name}${value ? ` - ${value}` : ''}`;
                }
                
                return (
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                        {displayText}
                    </Typography>
                );
            }
        },
        { label: 'Nhà cung cấp', field: 'supplier_name', minWidth: 160 },
        { 
            field: 'amount', 
            label: 'Số tiền', 
            minWidth: 80, 
            align: 'right',
            format: (value, row) => {
                const isCollection = row.payable_type === 'COLLECTION';
                
                return (
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            fontWeight: 'bold',
                            color: isCollection ? '#9c27b0' : '#d32f2f' 
                        }}
                    >
                        {formatCurrency(parseFloat(value))}
                    </Typography>
                );
            }
        },
        { label: 'Cần TT', field: 'real_payable', minWidth: 130, align: 'right' },
        { 
            label: 'Trạng thái', 
            field: 'status', 
            minWidth: 80, 
            align: 'center',
            format: (value, row) => {
                let label = PAYMENT_STATUS_LABELS[value] || value;
                let color = 'warning';
                let variant = 'outlined';

                if (value === 'PAID' || value === 'SETTLED') {
                    color = 'success';
                    label = 'Đã TT';
                    variant = 'filled';
                } 
                else if (value === 'APPROVED' || value === 'CONFIRMED') {
                    color = 'info';
                    label = 'Đã duyệt';
                    variant = 'filled';
                }

                if (row.payable_type === 'COLLECTION' && value === 'PENDING') {
                    label = 'Chưa duyệt';
                    color = 'warning';
                    variant = 'outlined';
                }

                return <Chip label={label} size="small" color={color} variant={variant} />;
            }
        },
        { label: 'Bill', minWidth: 60, align: 'center' },
        { label: '', minWidth: 80, align: 'center' },
    ];

    const getReportColumns = () => {
        return [
            { header: 'Mã BK', width: '12%', render: r => r.booking_code },
            { header: 'Khách hàng', width: '15%', render: r => r.customer_name },
            { header: 'Ngày đi', width: '10%', render: r => formatDateDisplay(r.start_date) },
            { header: 'Loại', width: '10%', render: r => PAYABLE_TYPE_LABELS[r.payable_type] || r.payable_type },
            { header: 'Ghi chú', width: '20%', render: r => {
                let text = r.note || '';
                if (r.payable_type === 'EXTRA' && r.service_name) text = `${r.service_name} - ${text}`;
                return text;
            }},
            { header: 'Nhà cung cấp', width: '15%', render: r => r.supplier_name },
            { header: 'Số tiền', width: '10%', render: r => formatCurrency(r.amount) },
            { header: 'Trạng thái', width: '8%', render: r => {
                if (r.status === 'PAID') return 'Đã TT';
                if (r.status === 'APPROVED') return 'Đã duyệt';
                return 'Chưa TT';
            }}
        ];
    };

    return (
        <Box>
            <Paper sx={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 3, paddingRight: 3, marginBottom: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}><CustomLabel title="Từ ngày (Ngày đi)" /><CustomDatePicker value={uiStartDate} onChange={(e) => setUiStartDate(e.target.value)} disabled={uiIsAllTime} /></Box>
                    <Box sx={{ flex: 1 }}><CustomLabel title="Đến ngày" /><CustomDatePicker value={uiEndDate} onChange={(e) => setUiEndDate(e.target.value)} disabled={uiIsAllTime} /></Box>
                    <Box sx={{ flex: 1 }}><CustomLabel title="Nhà cung cấp" /><Autocomplete size="small" options={suppliers} getOptionLabel={opt => opt.name || ''} value={uiFilterSupplier} onChange={(e, val) => setUiFilterSupplier(val)} renderInput={params => <TextField {...params} placeholder="Chọn NCC..." />} /></Box>
                    <Button variant="contained" onClick={handleApplyFilter} sx={{ bgcolor: '#007373', height: 40, px: 3, fontWeight: 'bold', marginTop: '24px', borderRadius: 2 }}>Áp dụng bộ lọc</Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Box sx={{ pt: 2.5 }}>
                        <FormControlLabel control={<Checkbox checked={uiIsAllTime} onChange={(e) => setUiIsAllTime(e.target.checked)} sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' } }} />} label={<Typography variant="body2" fontWeight="bold">Toàn thời gian</Typography>} sx={{ m: 0 }} />
                    </Box>
                    <Box sx={{ flex: 1, borderLeft: '1px solid #eee', pl: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}><CustomLabel title="Trạng thái TT" /><IconButton size="small" onClick={handleToggleAllStatus} sx={{ p: 0, color: '#007373' }}>{uiFilterPaymentStatus.length === Object.keys(PAYMENT_STATUS_LABELS).length ? <RemoveDoneIcon fontSize="small" /> : <DoneAllIcon fontSize="small" />}</IconButton></Box>
                        <Box sx={{ display: 'flex', gap: 4 }}>{Object.keys(PAYMENT_STATUS_LABELS).map(key => (<FormControlLabel key={key} control={<Checkbox size="small" checked={uiFilterPaymentStatus.includes(key)} onChange={() => handleToggleStatus(key)} sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' }, p: 0.5 }} />} label={<Typography variant="body2">{PAYMENT_STATUS_LABELS[key]}</Typography>} sx={{ mr: 0 }} />))}</Box>
                    </Box>
                    <Box sx={{ flex: 1, borderLeft: '1px solid #eee', pl: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}><CustomLabel title="Loại công nợ" /><IconButton size="small" onClick={handleToggleAllTypes} sx={{ p: 0, color: '#007373' }}>{uiFilterType.length === Object.keys(PAYABLE_TYPE_LABELS).length ? <RemoveDoneIcon fontSize="small" /> : <DoneAllIcon fontSize="small" />}</IconButton></Box>
                        <Box sx={{ display: 'flex', gap: 4 }}>{Object.keys(PAYABLE_TYPE_LABELS).map(key => (<FormControlLabel key={key} control={<Checkbox size="small" checked={uiFilterType.includes(key)} onChange={() => handleToggleType(key)} sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' }, p: 0.5 }} />} label={<Typography variant="body2">{PAYABLE_TYPE_LABELS[key]}</Typography>} sx={{ mr: 0 }} />))}</Box>
                    </Box>
                </Box>

                {selectedIds.length > 0 && (
                    <Box sx={{ p: 2, mt: 3, mb: 2, bgcolor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                                Đã chọn: {bulkActionStats.count} khoản
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.9rem' }}>
                                <span style={{ color: '#d32f2f' }}>Nợ: {formatCurrency(bulkActionStats.totalDebt)}</span>
                                {bulkActionStats.totalCollection > 0 && (
                                    <>
                                        <span style={{ color: '#666' }}>-</span>
                                        <span style={{ color: '#9c27b0' }}>Thu hộ: {formatCurrency(bulkActionStats.totalCollection)}</span>
                                    </>
                                )}
                                <span style={{ color: '#666' }}>=</span>
                                <strong style={{ color: bulkActionStats.netAmount >= 0 ? '#d32f2f' : '#2e7d32', fontSize: '1.1rem' }}>
                                    THỰC TRẢ: {formatCurrency(bulkActionStats.netAmount)}
                                </strong>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button 
                                variant="contained" 
                                color="warning" 
                                startIcon={<AssignmentIcon />} 
                                onClick={() => setPayableInfoOpen(true)}
                                sx={{ fontWeight: 'bold' }}
                            >
                                Tạo thông tin công nợ
                            </Button>

                            <Button 
                                variant="contained" 
                                color="primary" 
                                startIcon={<DoneAllIcon />} 
                                onClick={handleBulkApprove} // Cần định nghĩa hàm này
                                disabled={bulkActionStats.count === 0}
                                sx={{ fontWeight: 'bold', bgcolor: '#1976d2' }}
                            >
                                Duyệt công nợ
                            </Button>

                            <Button 
                                variant="contained" 
                                color="error" 
                                startIcon={<PaymentIcon />} 
                                onClick={handleBulkPayClick}
                                disabled={
                                    bulkActionStats.count === 0 || 
                                    bulkActionStats.hasDifferentSuppliers || 
                                    bulkActionStats.hasPaidItems
                                }
                            >
                                {bulkActionStats.hasPaidItems ? "Đã có khoản Đã TT" : 
                                 bulkActionStats.hasDifferentSuppliers ? "Khác NCC" : 
                                 bulkActionStats.netAmount > 0 ? "Thanh toán & Cấn trừ" : "Cấn trừ hoàn tất"}
                            </Button>
                        </Box>
                    </Box>
                )}

                <Box sx={{ mt: selectedIds.length > 0 ? 0 : 3 }}>
                    <DataTable
                        columns={columns} 
                        data={filteredData} 
                        summaryData={summaryData}
                        renderRow={(row) => (
                            <TableRow key={row.id} hover>
                                <TableCell padding="checkbox">
                                    <Checkbox 
                                        checked={selectedIds.includes(row.id)} 
                                        onChange={(e) => { 
                                            if (e.target.checked) setSelectedIds([...selectedIds, row.id]); 
                                            else setSelectedIds(selectedIds.filter(id => id !== row.id)); 
                                        }} 
                                    />
                                </TableCell>
                                <TableCell>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#007373' }}>
                                            {row.booking_code}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#555', display: 'block', lineHeight: 1 }}>
                                            {row.customer_name}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>{formatDateDisplay(row.start_date)}</TableCell>
                                <TableCell>
                                    <Chip 
                                        label={PAYABLE_TYPE_LABELS[row.payable_type] || (row.payable_type && row.payable_type.includes('TRANSPORT') ? 'Xe' : row.payable_type)}
                                        color={row.payable_type === 'COLLECTION' ? 'secondary' : 'default'}
                                        size="small"
                                        variant="outlined"
                                        sx={row.payable_type === 'COLLECTION' ? { borderColor: '#9c27b0', color: '#9c27b0', fontWeight: 'bold' } : {}}
                                    />
                                </TableCell>
                                
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                                        {(() => {
                                            let displayText = row.note || '';
                                            if (row.payable_type === 'EXTRA' && row.service_name) {
                                                displayText = `${row.service_name}${row.note ? ` - ${row.note}` : ''}`;
                                            }
                                            return displayText;
                                        })()}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{row.supplier_name}</TableCell>
                                <TableCell align="right">
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            fontWeight: 'bold',
                                            color: row.payable_type === 'COLLECTION' ? '#9c27b0' : '#d32f2f' 
                                        }}
                                    >
                                        {formatCurrency(row.amount)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {(() => {
                                        const total = parseFloat(row.amount || 0);
                                        const collected = parseFloat(row.collected_amount || 0);
                                        const real = total - collected;

                                        if (real < 0) {
                                            return (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.2 }}>
                                                    <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>{formatCurrency(Math.abs(real))}</span>
                                                    <span style={{ fontSize: '0.7rem', color: '#d32f2f', fontStyle: 'italic' }}>(NCC thu hộ)</span>
                                                </Box>
                                            );
                                        } else if (real > 0) {
                                            return <span style={{ color: '#1b5e20', fontWeight: 'bold' }}>{formatCurrency(real)}</span>;
                                        }
                                        return <span style={{ color: '#999' }}>0</span>;
                                    })()}
                                </TableCell>
                                <TableCell align="center">
                                    {(() => {
                                        let label = PAYMENT_STATUS_LABELS[row.status] || row.status;
                                        let color = 'warning';
                                        let variant = 'outlined';

                                        if (row.status === 'PAID' || row.status === 'SETTLED') {
                                            color = 'success';
                                            label = 'Đã TT';
                                            variant = 'filled';
                                        } 
                                        else if (row.status === 'APPROVED' || row.status === 'CONFIRMED') {
                                            color = 'info';
                                            label = 'Đã duyệt';
                                            variant = 'filled';
                                        }

                                        if (row.payable_type === 'COLLECTION' && row.status === 'PENDING') {
                                            label = 'Chưa duyệt';
                                            color = 'warning';
                                            variant = 'outlined';
                                        }

                                        return <Chip label={label} size="small" color={color} variant={variant} />;
                                    })()}
                                </TableCell>
                                <TableCell align="center">
                                    {row.bill_image && (<IconButton size="small" color="info" onClick={() => handleViewBill(row)} title="Xem Bill"><VisibilityIcon fontSize="small" /></IconButton>)}
                                </TableCell>
                                <TableCell align="center">
                                    <Button size="small" variant="outlined" sx={{ minWidth: 'auto', p: '2px 8px', fontSize: '0.75rem' }} onClick={() => navigate('/form', { state: { bookingCodeToLoad: row.booking_code } })}>Xem</Button>
                                </TableCell>
                            </TableRow>
                        )}
                    />
                </Box>
            </Paper>
            <BillDetailDialog open={billModal.open} onClose={() => setBillModal({ ...billModal, open: false })} billData={billModal.data} bookingData={billModal.bookingData} />
            <PayableInfoDialog 
                open={payableInfoOpen}
                onClose={() => setPayableInfoOpen(false)}
                selectedPayables={selectedPayables}
                suppliers={suppliers}
                showToast={showToast}
                filterDate={{ start: uiStartDate, end: uiEndDate, isAllTime: uiIsAllTime }}
            />
            <BulkPaymentDialog 
                open={bulkPayOpen}
                onClose={() => setBulkPayOpen(false)}
                items={selectedPayables.map(p => {
                    const supInfo = suppliers.find(s => s.id === p.supplier);
                    return {
                        id: p.id,
                        amount: p.amount,
                        payable_type: p.payable_type,
                        supplier_name: p.supplier_name,
                        bank_info: supInfo ? supInfo.bank_info : '',
                        receiver_info: p.receiver_info
                    };
                })}
                type="PAYABLE"           // Chế độ PAYABLE
                showToast={showToast} // 1. Truyền hàm showToast vào
                onSuccess={() => {
                    setSelectedIds([]);
                    loadData();
                    showToast("Đã thanh toán gộp thành công!", "success");
                }}
            />
            <ConfirmDialog 
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
                title={confirmDialog.title}
                content={confirmDialog.content}
                onConfirm={confirmDialog.onConfirm}
                confirmText="Xác nhận duyệt"
                confirmColor="primary"
            />
            {(() => {
                const totalAmount = filteredData.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
                const summaryRowData = {
                    booking_code: 'TỔNG CỘNG:',
                    amount: formatCurrency(totalAmount)
                };
                
                return (
                    <ReportExportDialog 
                        open={reportOpen}
                        onClose={() => setReportOpen(false)}
                        type="PAYABLE_DEBT" 
                        data={filteredData}
                        dateRange={{ start: activeFilters.startDate, end: activeFilters.endDate }}
                        customColumns={getReportColumns()}
                        summaryRow={summaryRowData} /* <--- THÊM DÒNG TỔNG */
                        fileNamePrefix="BaoCao_CongNo"
                        customTitle="BÁO CÁO TỔNG QUAN CÔNG NỢ"
                    />
                );
            })()}
            <Snackbar 
                open={toast.open} 
                autoHideDuration={3000} 
                onClose={handleCloseToast} 
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }} 
                sx={{ mt: 8 }}
            >
                <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', boxShadow: 3 }} variant="filled">
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
});

export default PayableListTab;